<template>
  <layout-basic>
    <section class="buy-now-section">
      <v-container>
        <div class="buy-now-header">
          <div class="price-banner">GET STARTED</div>
          <h1>Get started with IMPROVE</h1>
          <p class="buy-now-lead">
            Choose your plan here, then sign your agreement and pay on the
            IMPROVE website. Your account is activated the moment payment is
            confirmed, with everything you set up during your trial.
          </p>
        </div>

        <v-row class="buy-now-grid">
          <v-col cols="12" md="7" lg="8" class="order-last order-md-first">
            <v-card class="buy-now-card" flat>
              <div class="card-heading">
                <span class="step-index">1</span>
                <div>
                  <h2>Choose your plan</h2>
                  <p>
                    A three year agreement, billed annually per business: the
                    year 1 price below, then years 2 and 3 at the renewal price.
                  </p>
                </div>
              </div>

              <div class="plan-tiles">
                <button
                  v-for="p in planOptions"
                  :key="p.slug"
                  type="button"
                  class="plan-tile"
                  :class="{ selected: plan === p.slug }"
                  :disabled="loading"
                  @click="plan = p.slug"
                >
                  <span class="plan-tile-check" aria-hidden="true">✔</span>
                  <span class="plan-tile-name">{{ p.name }}</span>
                  <span class="plan-tile-users">{{ p.users }}</span>
                  <span class="plan-tile-price">{{ p.price }}<small> year 1</small></span>
                  <span v-if="p.renewal" class="plan-tile-renewal">then {{ p.renewal }} a year for years 2 and 3</span>
                </button>
              </div>

              <v-btn
                :loading="loading"
                class="buy-now-submit-btn mt-8"
                block
                depressed
                @click="continueToAgreement"
              >
                Continue to your agreement
              </v-btn>

              <p class="secure-note">
                You will be taken to the IMPROVE website with your details
                filled in, to read and sign your agreement and then pay by card
                on Stripe. Card details are entered on Stripe, never stored by us.
              </p>
            </v-card>
          </v-col>

          <v-col cols="12" md="5" lg="4" class="order-first order-md-last">
            <div class="summary-sticky">
              <v-card class="summary-card" flat>
                <div class="summary-head">
                  <span class="summary-eyebrow">YOUR PLAN</span>
                  <h3 class="summary-plan-name">
                    {{ selectedPlan ? selectedPlan.name : "IMPROVE" }}
                  </h3>
                  <p class="summary-users">
                    {{ selectedPlan ? selectedPlan.users : "" }}
                  </p>
                  <div class="summary-price">
                    <span class="summary-amount">{{
                      selectedPlan ? selectedPlan.price : ""
                    }}</span>
                    <span class="summary-period">year 1</span>
                  </div>
                  <p v-if="selectedPlan && selectedPlan.renewal" class="summary-perbiz">
                    then {{ selectedPlan.renewal }} a year for years 2 and 3
                  </p>
                  <p v-else class="summary-perbiz">Per business, billed annually</p>
                </div>

                <div class="summary-body">
                  <p class="summary-included-label">Included in every plan</p>
                  <ul class="summary-features">
                    <li v-for="f in includedFeatures" :key="f">
                      <span class="feat-check" aria-hidden="true">✔</span>{{ f }}
                    </li>
                  </ul>

                  <!-- Hidden when the catalogue loaded WITHOUT a launch fee:
                       a fee withdrawn in Stripe must not still be advertised. -->
                  <div
                    v-if="launchFee || !catalogueLoaded"
                    class="summary-line"
                  >
                    <span>{{
                      launchFee ? launchFee.name : "IMPROVE Launch Programme"
                    }}</span>
                    <span class="summary-line-value">{{
                      (launchFee ? launchFee.amount : "£695") + " one-off"
                    }}</span>
                  </div>

                  <div v-if="totalInvestment" class="summary-line summary-line-total">
                    <span>Your total IMPROVE investment (3 years)</span>
                    <span class="summary-line-value">{{ totalInvestment }}</span>
                  </div>

                  <div class="summary-foot">
                    <p class="summary-reassure">
                      Year 1 and the Launch Programme are charged today. Years 2
                      and 3 are invoiced at the start of each year, as set out in
                      your agreement.
                    </p>
                    <p class="summary-vat">All prices exclusive of VAT, which is added at checkout.</p>
                  </div>
                </div>
              </v-card>

              <p class="summary-help">
                Not sure which plan?
                <a href="mailto:help@improve.online">Talk to our team</a>
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </layout-basic>
</template>

<script>
import LayoutBasic from "@/layouts/Dashboard";
import billingAPI from "@/api/billing.js";
import { IMPROVE_CONFIG } from "@/config.js";

// DISPLAY FALLBACK ONLY. Stripe is the source of truth for plans and prices
// (product metadata + default price); fetchCatalogue() replaces these on load.
// These values are used only when the catalogue endpoint cannot be reached, so
// a Stripe reprice can never leave this page quoting an amount that differs
// from what checkout actually charges. The renewal figures are the September
// 2026 brochure, the same fallback the marketing site uses.
const FALLBACK_PLANS = {
  essential: {
    slug: "essential",
    name: "Essential",
    price: "£995",
    pence: 99500,
    renewal: "£1,695",
    renewalPence: 169500,
    users: "1-50 users",
    banding: 23
  },
  growth: {
    slug: "growth",
    name: "Growth",
    price: "£1,695",
    pence: 169500,
    renewal: "£2,495",
    renewalPence: 249500,
    users: "51-100 users",
    banding: 24
  },
  unlimited: {
    slug: "unlimited",
    name: "Unlimited",
    price: "£2,995",
    pence: 299500,
    renewal: "£3,995",
    renewalPence: 399500,
    users: "101-250 users",
    banding: 25
  }
};
const FALLBACK_LAUNCH_FEE_PENCE = 69500;

/** 169500 -> "£1,695"; 99950 -> "£999.50". Money stays pence until display. */
function formatPounds(pence) {
  const pounds = pence / 100;
  const hasPennies = pence % 100 !== 0;
  return (
    "£" +
    pounds.toLocaleString("en-GB", {
      minimumFractionDigits: hasPennies ? 2 : 0,
      maximumFractionDigits: hasPennies ? 2 : 0
    })
  );
}

/**
 * The app's side of buying IMPROVE. This page only CHOOSES the plan; the
 * purchase itself (agreement, signature, payment) happens on the website's
 * /get-started flow, where the buyer's trial details are prefilled and the
 * paid webhook upgrades the account they already have.
 *
 * It used to run its own Stripe checkout from here. That charged the year 1
 * price with no launch fee and no signed agreement, while the subscription it
 * created still got the three year step up attached: the buyer was shown
 * "£1,695 a year" and would have been billed £2,495 in year 2 without ever
 * seeing it (staging test purchase, company 1626, 16 Sep 2026). One purchase
 * path, one agreement, one set of numbers.
 */
export default {
  name: "BillingCheckout",
  components: { LayoutBasic },
  data() {
    return {
      plan: "growth",
      loading: false,
      // Replaced wholesale by fetchCatalogue() with the live Stripe catalogue.
      plans: FALLBACK_PLANS,
      launchFee: null,
      launchFeePence: FALLBACK_LAUNCH_FEE_PENCE,
      catalogueLoaded: false,
      includedFeatures: [
        "Learning Management System",
        "Unlimited Courses",
        "Unlimited Playlists",
        "Reporting & Dashboards",
        "Certificates",
        "Mobile Friendly Access",
        "User Management",
        "Email Support"
      ]
    };
  },
  computed: {
    planOptions() {
      return Object.values(this.plans);
    },
    selectedPlan() {
      return this.plans[this.plan] || null;
    },
    /** Year 1 + 2 x years 2 and 3 + the launch fee, the figure the website prints. */
    totalInvestment() {
      const p = this.selectedPlan;
      if (!p || typeof p.pence !== "number" || typeof p.renewalPence !== "number") return "";
      const fee = this.catalogueLoaded && !this.launchFee ? 0 : this.launchFeePence;
      return formatPounds(p.pence + 2 * p.renewalPence + fee);
    }
  },
  created() {
    const q = (this.$route.query.plan || "").toLowerCase();
    if (this.plans[q]) this.plan = q;
    this.fetchCatalogue(q);
  },
  methods: {
    // Replace the display fallback with the live Stripe catalogue. Silent on
    // failure: an admin trying to buy should never be shown an error because a
    // price lookup blipped, and the fallback still renders.
    fetchCatalogue(preselect) {
      billingAPI
        .catalogue()
        .then(res => {
          const data = (res && res.data) || {};
          const list = Array.isArray(data.plans) ? data.plans : [];
          const plans = {};
          list.forEach(p => {
            if (!p || !p.slug || typeof p.amount_pence !== "number") return;
            const hasRenewal = typeof p.renewal_amount_pence === "number";
            plans[p.slug] = {
              slug: p.slug,
              name: String(p.name || "").replace(/^improve\s+/i, ""),
              price: formatPounds(p.amount_pence),
              pence: p.amount_pence,
              renewal: hasRenewal ? formatPounds(p.renewal_amount_pence) : "",
              renewalPence: hasRenewal ? p.renewal_amount_pence : null,
              users: p.users_label || "",
              banding: p.banding
            };
          });
          if (!Object.keys(plans).length) return;
          this.plans = plans;
          this.catalogueLoaded = true;
          if (data.launch_fee && typeof data.launch_fee.amount_pence === "number") {
            this.launchFee = {
              name: data.launch_fee.name || "IMPROVE Launch Programme",
              amount: formatPounds(data.launch_fee.amount_pence)
            };
            this.launchFeePence = data.launch_fee.amount_pence;
          } else {
            this.launchFee = null;
            this.launchFeePence = 0;
          }
          if (preselect && plans[preselect]) {
            this.plan = preselect;
          } else if (!plans[this.plan]) {
            this.plan = Object.keys(plans)[0];
          }
        })
        .catch(() => {
          /* keep FALLBACK_PLANS */
        });
    },
    /**
     * Hand off to the website's purchase flow with what the app knows about
     * this admin, so nothing is retyped. The website reads these from the
     * query string, shows "Upgrading your trial account", and the API
     * upgrades this company on payment.
     */
    continueToAgreement() {
      if (!this.selectedPlan) {
        this.$snotify.error("Please choose a plan.");
        return;
      }
      const base = String(IMPROVE_CONFIG.FRONTEND_URL || "").replace(/\/+$/, "");
      if (!base) {
        this.$snotify.error("The purchase page address is not configured. Please email help@improve.online.");
        return;
      }
      const u = this.$store.getters.user || {};
      const company = u.company && u.company.data ? u.company.data : {};
      const params = new URLSearchParams();
      params.set("plan", this.plan);
      if (u.email) params.set("email", u.email);
      if (u.first_name) params.set("first", u.first_name);
      if (u.last_name) params.set("last", u.last_name);
      if (company.company_name) params.set("company", company.company_name);
      this.loading = true;
      window.location = base + "/get-started?" + params.toString();
    }
  }
};
</script>

<style scoped>
.buy-now-section {
  --navy: #002458;
  --teal: #08a0a8;
  --grey: #6b7280;
  --line: #e6ebf1;
  --tint: #eef8f8;
  background: #f7f9fb;
  padding: 32px 0 72px;
}

.buy-now-header {
  text-align: center;
  max-width: 640px;
  margin: 0 auto 36px;
}

.buy-now-header .price-banner {
  width: 120px;
  height: 22px;
  background-color: var(--teal);
  color: #ffffff;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px;
  margin: 0 auto 18px;
}

.buy-now-header h1 {
  font-size: 36px;
  line-height: 1.15;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 14px;
}

.buy-now-lead {
  font-size: 16px;
  color: var(--grey);
  margin: 0;
}

.billing-disabled-alert a {
  color: var(--navy);
  font-weight: 700;
}

.buy-now-card {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 30px 30px 34px;
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.06);
}

.card-heading {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
}

.card-heading .step-index {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--tint);
  color: var(--teal);
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-heading h2 {
  font-size: 21px;
  font-weight: 700;
  color: var(--navy);
  line-height: 1.2;
  margin: 2px 0 2px;
}

.card-heading p {
  font-size: 14px;
  color: var(--grey);
  margin: 0;
}

.plan-tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.plan-tile {
  position: relative;
  text-align: left;
  background: #ffffff;
  border: 1.5px solid var(--line);
  border-radius: 12px;
  padding: 16px 16px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  display: flex;
  flex-direction: column;
}

.plan-tile:hover:not(:disabled) {
  border-color: var(--teal);
}

.plan-tile:focus {
  outline: none;
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(8, 160, 168, 0.18);
}

.plan-tile.selected {
  border-color: var(--navy);
  background: #fbfdff;
  box-shadow: 0 6px 18px rgba(0, 36, 88, 0.1);
}

.plan-tile:disabled {
  cursor: default;
  opacity: 0.7;
}

.plan-tile-check {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--navy);
  color: #ffffff;
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.plan-tile.selected .plan-tile-check {
  opacity: 1;
  transform: scale(1);
}

.plan-tile-name {
  font-weight: 700;
  font-size: 16px;
  color: var(--navy);
}

.plan-tile-users {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--teal);
  margin: 3px 0 10px;
}

.plan-tile-price {
  font-size: 22px;
  font-weight: 700;
  color: var(--navy);
  margin-top: auto;
}

.plan-tile-renewal {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.35;
  color: var(--grey);
}

.plan-tile-price small {
  font-size: 12px;
  font-weight: 600;
  color: var(--grey);
}

.buy-now-submit-btn {
  background-color: var(--navy) !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  letter-spacing: 0.01em !important;
  border-radius: 8px !important;
  height: 52px !important;
}

.buy-now-submit-btn:hover:not(:disabled) {
  background-color: var(--teal) !important;
}

.secure-note {
  font-size: 12.5px;
  color: var(--grey);
  margin: 16px 0 0;
}

.summary-sticky {
  position: sticky;
  top: 90px;
}

.summary-card {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--line);
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.08);
}

.summary-head {
  background: linear-gradient(135deg, #002458 0%, #0b3a74 100%);
  color: #ffffff;
  padding: 26px 26px 24px;
}

.summary-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #7fd6db;
}

.summary-plan-name {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 8px 0 2px;
}

.summary-users {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a9c2e6;
  margin: 0 0 14px;
}

.summary-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.summary-amount {
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  color: #ffffff;
}

.summary-period {
  font-size: 15px;
  font-weight: 600;
  color: #a9c2e6;
}

.summary-perbiz {
  font-size: 13px;
  color: #cbd9ee;
  margin: 8px 0 0;
}

.summary-body {
  background: #ffffff;
  padding: 22px 26px 24px;
}

.summary-included-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--grey);
  margin: 0 0 14px;
}

.summary-features {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
}

.summary-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14.5px;
  color: #2c3a4b;
  padding: 6px 0;
}

.feat-check {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--tint);
  color: var(--teal);
  font-size: 10px;
  line-height: 18px;
  text-align: center;
  margin-top: 1px;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-top: 1px dashed var(--line);
  font-size: 14.5px;
  color: var(--navy);
  font-weight: 600;
}

.summary-line-total {
  font-weight: 700;
  color: var(--navy);
  border-top: 1px solid var(--line);
  padding-top: 12px;
  margin-top: 4px;
}

.summary-line-value {
  color: var(--teal);
  font-weight: 700;
}

.summary-foot {
  border-top: 1px solid var(--line);
  padding-top: 16px;
}

.summary-reassure {
  font-size: 13.5px;
  color: #2c3a4b;
  margin: 0 0 6px;
}

.summary-vat {
  font-size: 12px;
  color: var(--grey);
  margin: 0;
}

.summary-help {
  text-align: center;
  font-size: 13.5px;
  color: var(--grey);
  margin: 16px 0 0;
}

.summary-help a {
  color: var(--teal);
  text-decoration: none;
  font-weight: 600;
}

@media screen and (max-width: 960px) {
  .summary-sticky {
    position: static;
    margin-bottom: 24px;
  }
}

@media screen and (max-width: 600px) {
  .buy-now-section {
    padding: 24px 0 56px;
  }

  .buy-now-header h1 {
    font-size: 28px;
  }

  .buy-now-card {
    padding: 22px 18px 26px;
  }

  .plan-tiles {
    grid-template-columns: 1fr;
  }

  .plan-tile {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 14px;
    padding: 14px 16px;
  }

  .plan-tile-users {
    margin: 0;
    order: 3;
    flex-basis: 100%;
  }

  .plan-tile-price {
    margin-left: auto;
  }
}
</style>
